import {
    Component,
    Input,
    Output,
    EventEmitter,
    OnInit,
    signal,
    computed,
    ChangeDetectionStrategy,
} from "@angular/core";

import { FormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { IngestionFieldDefinition } from "../data-ingestion/data.ingestion.service";
import { S25Util } from "../../util/s25-util";
import { WorkflowUtil } from "./workflow.util";
import { WorkflowFieldReferenceHelperComponent } from "./workflow.field.reference.helper.component";

interface FunctionDefinition {
    name: string;
    description: string;
    parameters: number;
    example: string;
    defaultValue: string;
}

@TypeManagerDecorator("workflow-json-payload-builder")
@Component({
    selector: "workflow-json-payload-builder",
    template: `
        <div class="payload-builder">
            <div class="builder-header">
                <h3>JSON Payload Builder</h3>
                <div class="validation-status" [class.valid]="isValidJson()" [class.invalid]="!isValidJson()">
                    {{ isValidJson() ? "Valid JSON" : "Invalid JSON" }}
                </div>
            </div>

            <div class="builder-content">
                <div class="field-picker">
                    <h4>Fields</h4>

                    <!-- Field Reference Helper -->
                    <label>Field Reference Helper</label>
                    <workflow-field-reference-helper
                        [formFields]="availableFields"
                        helpText="Select a field to copy its reference (e.g., $CNET-123-FIELD_CODE) to clipboard for pasting into JSON payload."
                        (fieldReferenceCopied)="onFieldReferenceCopied($event)"
                    />
                </div>

                <div class="function-picker">
                    <h4>Functions</h4>
                    <select
                        [(ngModel)]="selectedFunctionName"
                        class="function-select"
                        (ngModelChange)="onFunctionSelected($event)"
                    >
                        <option value="">Select function to copy...</option>
                        @for (func of availableFunctions(); track func.name) {
                            <option [value]="func.name">{{ func.name }}</option>
                        }
                    </select>
                    @if (copiedFunctionName()) {
                        <span class="copied-message">Function Copied!</span>
                    }
                    @if (selectedFunctionName) {
                        <div class="function-help">
                            <div class="function-help-header">
                                <h5>{{ selectedFunctionName }}</h5>
                                <button type="button" class="clear-btn" (click)="clearFunctionSelection()">×</button>
                            </div>
                            <div class="function-description">{{ getSelectedFunctionDescription() }}</div>
                            <div class="function-example">{{ getSelectedFunctionExample() }}</div>
                        </div>
                    }
                </div>

                <div class="json-editor">
                    <h4>JSON Payload</h4>
                    <textarea
                        [(ngModel)]="jsonPayloadModel"
                        (ngModelChange)="onJsonChange($event)"
                        class="json-textarea"
                        placeholder="Enter your JSON payload here..."
                        rows="20"
                        cols="75"
                    ></textarea>

                    @if (validationError()) {
                        <div class="validation-error">{{ validationError() }}</div>
                    }
                </div>
            </div>

            <div class="builder-footer">
                <button type="button" class="btn btn-primary" [disabled]="!isValidJson()" (click)="savePayload()">
                    Save Payload
                </button>
                <button type="button" class="btn btn-secondary" (click)="resetPayload()">Reset</button>
            </div>
        </div>
    `,
    styles: `
        .payload-builder {
            display: flex;
            flex-direction: column;
            height: 100%;
            border: 1px solid var(--color--border);
            border-radius: 6px;
        }

        .builder-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 16px;
            background: var(--color--surface-2-bg);
            border-bottom: 1px solid var(--color--border);
        }

        .builder-header h3 {
            margin: 0;
            color: var(--color--text-strong);
        }

        .validation-status {
            padding: 4px 8px;
            border-radius: 4px;
            font-size: 12px;
            font-weight: 500;
        }

        .validation-status.valid {
            background: var(--color--registration);
            color: white;
        }

        .validation-status.invalid {
            background: var(--color--danger);
            color: white;
        }

        .builder-content {
            display: flex;
            flex: 1;
            overflow: hidden;
        }

        .field-picker,
        .function-picker {
            width: 250px;
            border-right: 1px solid var(--color--border);
            padding: 16px;
            overflow-y: auto;
        }

        .json-editor {
            flex: 1;
            padding: 16px;
            display: flex;
            flex-direction: column;
        }

        .field-picker h4,
        .function-picker h4,
        .json-editor h4 {
            margin: 0 0 12px 0;
            color: var(--color--text-strong);
            font-size: 14px;
        }

        .function-select {
            width: 100%;
            padding: 8px 12px;
            border: 1px solid var(--color--border-bold);
            border-radius: 4px;
            font-size: 14px;
            margin-bottom: 8px;
        }

        .copied-message {
            color: var(--color--registration);
            font-size: 12px;
            font-weight: 500;
        }

        .function-help {
            margin-top: 12px;
            padding: 12px;
            background: var(--color--surface-2-bg);
            border: 1px solid var(--color--border);
            border-radius: 4px;
        }

        .function-help-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 8px;
        }

        .function-help-header h5 {
            margin: 0;
            font-size: 13px;
            color: var(--color--text-strong);
        }

        .clear-btn {
            background: var(--color--danger);
            color: white;
            border: none;
            border-radius: 50%;
            width: 18px;
            height: 18px;
            font-size: 12px;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
        }

        .function-description {
            font-size: 12px;
            color: var(--color--text);
            margin-bottom: 8px;
        }

        .function-example {
            font-size: 11px;
            color: var(--color--text-muted);
            font-family: var(--font--pre), monospace;
            background: var(--color--surface-bg);
            padding: 6px;
            border-radius: 3px;
            border: 1px solid var(--color--border);
        }

        .json-textarea {
            flex: 1;
            font-family: var(--font--pre), monospace;
            font-size: 14px;
            border: 1px solid var(--color--border-bold);
            border-radius: 4px;
            padding: 12px;
            resize: none;
            outline: none;
        }

        .json-textarea:focus {
            border-color: var(--color--link-text);
            box-shadow: var(--shadow--form-focus);
        }

        .validation-error {
            margin-top: 12px;
            padding: 12px;
            background: var(--color--danger);
            color: white;
            border-radius: 4px;
            font-size: 14px;
        }

        .builder-footer {
            padding: 16px;
            background: var(--color--surface-2-bg);
            border-top: 1px solid var(--color--border);
            display: flex;
            gap: 10px;
        }

        .btn {
            padding: 8px 16px;
            border: 1px solid var(--color--border-bold);
            border-radius: 4px;
            cursor: pointer;
            font-size: 14px;
            transition: all 0.2s;
        }

        .btn-primary {
            background: var(--color--link-text);
            color: white;
            border-color: var(--color--link-text);
        }

        .btn-primary:hover:not(:disabled) {
            background: var(--color--link-text-hover);
        }

        .btn-primary:disabled {
            background: var(--color--text-muted);
            border-color: var(--color--text-muted);
            cursor: not-allowed;
        }

        .btn-secondary {
            background: var(--color--surface-bg);
            color: var(--color--text-muted);
        }

        .btn-secondary:hover {
            background: var(--color--surface-2-bg);
        }
    `,
    standalone: true,
    imports: [FormsModule, WorkflowFieldReferenceHelperComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WorkflowJsonPayloadBuilderComponent implements OnInit {
    @Input() availableFields: IngestionFieldDefinition[] = [];
    @Input() initialPayload: string = "";

    @Output() payloadChange = new EventEmitter<string>();
    @Output() payloadSave = new EventEmitter<string>();

    jsonPayload = signal("");
    validationError = signal("");

    // Property for ngModel binding
    jsonPayloadModel = "";

    copiedFunctionName = signal<string | null>(null);

    // Properties for select dropdowns - needed for ngModel binding and change detection
    selectedFunctionName: string = "";

    availableFunctions = signal<FunctionDefinition[]>([
        {
            name: "$CNET-fn-split",
            description: "Split a string by a separator",
            parameters: 2,
            example: '$CNET-fn-split("value1,value2", ",")',
            defaultValue: "[]",
        },
    ]);

    isValidJson = computed(() => {
        const payload = this.jsonPayload();
        if (!payload.trim()) return true; // Empty is valid

        try {
            // Replace field placeholders with default values for validation
            const validationPayload = this.replaceFieldPlaceholdersWithDefaults(payload);
            JSON.parse(validationPayload);
            return true;
        } catch (e) {
            return false;
        }
    });

    ngOnInit() {
        if (this.initialPayload) {
            this.jsonPayload.set(this.initialPayload);
            this.jsonPayloadModel = this.initialPayload;
        }
        // Generate fieldCode from fieldLabel for fields that don't have it
        this.availableFields.forEach((field) => {
            if (!field.fieldCode) {
                field.fieldCode = WorkflowUtil.generateFieldCode(field.fieldLabel || "");
            }
        });
    }

    onJsonChange(newValue: string) {
        this.jsonPayload.set(newValue);
        this.jsonPayloadModel = newValue;
        this.validateJson();
        this.payloadChange.emit(newValue);
    }

    validateJson() {
        const payload = this.jsonPayload();
        if (!payload.trim()) {
            this.validationError.set("");
            return;
        }

        try {
            // Replace field placeholders with default values for validation
            const validationPayload = this.replaceFieldPlaceholdersWithDefaults(payload);
            JSON.parse(validationPayload);
            this.validationError.set("");
        } catch (e) {
            this.validationError.set(`JSON Error: ${e.message}`);
        }
    }

    replaceFieldPlaceholdersWithDefaults(payload: string): string {
        let result = payload;

        // Replace field placeholders with default values based on field type
        result = result.replace(/\$CNET-(\d+)-([A-Za-z0-9]+)/g, (match, fieldId, fieldCode) => {
            const field = this.availableFields.find((f) => f.fieldDefinitionId.toString() === fieldId);
            if (field) {
                switch (field.answerType) {
                    case "INTEGER":
                        return "0";
                    case "FLOAT":
                        return "0.0";
                    case "BOOLEAN":
                        return "false";
                    case "DATETIME":
                        return '"2025-01-01T00:00:00Z"';
                    default:
                        return '""';
                }
            }
            return '""';
        });

        // Replace function placeholders with default return values
        // Use intelligent parentheses counting to handle nested function calls
        let replacementMade = true;
        while (replacementMade) {
            replacementMade = false;

            // Find the first occurrence of $CNET-fn-
            const fnPrefix = "$CNET-fn-";
            let startIndex = result.indexOf(fnPrefix);

            while (startIndex !== -1) {
                // Extract function name (everything between $CNET-fn- and the opening parenthesis)
                const fnNameStart = startIndex + fnPrefix.length;
                const openParenIndex = result.indexOf("(", fnNameStart);

                if (openParenIndex === -1) {
                    // No opening parenthesis found, skip this occurrence
                    startIndex = result.indexOf(fnPrefix, startIndex + 1);
                    continue;
                }

                const functionName = fnPrefix + result.substring(fnNameStart, openParenIndex);

                // Count parentheses to find the matching closing parenthesis
                let parenCount = 1;
                let currentIndex = openParenIndex + 1;

                while (currentIndex < result.length && parenCount > 0) {
                    const char = result.charAt(currentIndex);
                    if (char === "(") {
                        parenCount++;
                    } else if (char === ")") {
                        parenCount--;
                    }
                    currentIndex++;
                }

                if (parenCount === 0) {
                    // Found matching closing parenthesis
                    const endIndex = currentIndex; // currentIndex is now one past the closing paren

                    // Look up the function to get its default value
                    const func = this.availableFunctions().find((f) => f.name === functionName);
                    if (func) {
                        // Replace the entire function call with its default value
                        result = result.substring(0, startIndex) + func.defaultValue + result.substring(endIndex);
                        replacementMade = true;
                        break; // Start over from the beginning to handle any new function calls
                    }
                }

                // Move to next occurrence
                startIndex = result.indexOf(fnPrefix, startIndex + 1);
            }
        }

        return result;
    }

    copyFunction(func: FunctionDefinition, event: Event) {
        this.copyToClipboard(func.example);
        this.copiedFunctionName.set(func.name);
        setTimeout(() => this.copiedFunctionName.set(null), 2000);
    }

    copyToClipboard(text: string) {
        if (navigator.clipboard) {
            navigator.clipboard.writeText(text);
        } else {
            // Fallback for older browsers
            const textArea = document.createElement("textarea");
            textArea.value = text;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand("copy");
            document.body.removeChild(textArea);
        }
    }

    savePayload() {
        if (this.isValidJson()) {
            this.payloadSave.emit(this.jsonPayload());
        }
    }

    resetPayload() {
        this.jsonPayload.set("");
        this.jsonPayloadModel = "";
        this.validationError.set("");
    }

    onFunctionSelected(functionName: string) {
        if (!functionName) return;

        const func = this.availableFunctions().find((f) => f.name === functionName);
        if (func) {
            this.copyToClipboard(func.example);
            this.copiedFunctionName.set(func.name);
            setTimeout(() => this.copiedFunctionName.set(null), 2000);
        }
    }

    getSelectedFunctionDescription(): string {
        if (!this.selectedFunctionName) return "";
        const func = this.availableFunctions().find((f) => f.name === this.selectedFunctionName);
        return func ? func.description : "";
    }

    getSelectedFunctionExample(): string {
        if (!this.selectedFunctionName) return "";
        const func = this.availableFunctions().find((f) => f.name === this.selectedFunctionName);
        return func ? func.example : "";
    }

    clearFunctionSelection() {
        this.selectedFunctionName = "";
    }

    onFieldReferenceCopied(fieldReference: string): void {
        console.log("Field reference copied:", fieldReference);
        // The reusable component handles all the clipboard logic and UI feedback
        // This method is just for any additional logic if needed in the future
    }
}
